import Box from "@mui/material/Box";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableContainer from "../../../components/Tables/TableContainer";
import TableCell from "../../BillingAndPayments/components/TableCell";
import Switch from "../../../components/Form/Switch";
import Loader from "../../../components/Loaders/Loader";
import { useTranslationApp } from "../../../lib/i18next";
import {
  PROMOTIONAL_CODE_TYPE_APP_SUMO,
  PROMOTIONAL_CODE_TYPE_STANDARD,
  PROMOTIONAL_CODE_TYPE_STRIPE_DISCOUNT,
  PROMOTIONAL_CODE_DISCOUNT_TYPE_FIXED,
  PROMOTIONAL_CODE_DISCOUNT_TYPE_PERCENTAGE,
} from "../../../actions/owner";
import {
  COLUMN_NAME_IS_ACTIVE,
  DATE_SHORT,
  EMPTY_RESULTS_SHOW,
  OWNER_PROMOTIONAL_CODES_COLUMN_CODE,
  OWNER_PROMOTIONAL_CODES_COLUMN_CREATED,
  OWNER_PROMOTIONAL_CODES_COLUMN_MAX_REDEMPTIONS,
  OWNER_PROMOTIONAL_CODES_COLUMN_REGISTERED,
  OWNER_PROMOTIONAL_CODES_COLUMN_TYPE,
  OWNER_PROMOTIONAL_CODES_TYPE_APP_SUMO_TIER_SUFFIX,
  OWNER_PROMOTIONAL_CODES_TYPE_VALUE_APP_SUMO,
  OWNER_PROMOTIONAL_CODES_TYPE_VALUE_STANDARD,
  OWNER_PROMOTIONAL_CODES_TYPE_VALUE_STRIPE_DISCOUNT,
} from "../../../i18n/keysTranslations";

const headerCellProps = { showTooltip: false, isHeader: true };
const bodyCellProps = {
  showTooltip: false,
  isHeader: false,
  showLastRowBorder: true,
};

const firstColSx = {
  paddingLeft: "16px !important",
};

const formatCreated = ({ creationTime, t }) => {
  if (creationTime == null || Number.isNaN(Number(creationTime))) return "—";
  try {
    return t(DATE_SHORT, { date: new Date(Number(creationTime)) });
  } catch {
    return "—";
  }
};

const PromotionalCodesTable = ({
  rows,
  loading,
  loadingMore,
  hasMore,
  lastItemRef,
  onChangeStatus,
}) => {
  const { t } = useTranslationApp();

  const getTypeLabel = ({ type, metadata }) => {
    if (type === PROMOTIONAL_CODE_TYPE_APP_SUMO) {
      const baseLabel = t(OWNER_PROMOTIONAL_CODES_TYPE_VALUE_APP_SUMO);
      const tier = metadata?.tier;
      if (tier === undefined || tier === null || tier === "") {
        return baseLabel;
      }
      return `${baseLabel}${t(
        OWNER_PROMOTIONAL_CODES_TYPE_APP_SUMO_TIER_SUFFIX,
        {
          tier: String(tier),
        }
      )}`;
    }
    if (type === PROMOTIONAL_CODE_TYPE_STANDARD) {
      return t(OWNER_PROMOTIONAL_CODES_TYPE_VALUE_STANDARD);
    }
    if (type === PROMOTIONAL_CODE_TYPE_STRIPE_DISCOUNT) {
      const baseLabel = t(OWNER_PROMOTIONAL_CODES_TYPE_VALUE_STRIPE_DISCOUNT);
      const discount = metadata?.discount;
      const discountType = metadata?.discountType;
      const stripeCode = metadata?.stripeCode;
      if (discount == null || !discountType) {
        return baseLabel;
      }
      const discountLabel =
        discountType === PROMOTIONAL_CODE_DISCOUNT_TYPE_PERCENTAGE
          ? `${discount}%`
          : discountType === PROMOTIONAL_CODE_DISCOUNT_TYPE_FIXED
            ? `$${discount}`
            : String(discount);
      const stripeSuffix = stripeCode ? ` · ${stripeCode}` : "";
      return `${baseLabel} (${discountLabel}${stripeSuffix})`;
    }
    return type ? String(type) : "—";
  };

  return (
    <TableContainer
      loading={loading && rows.length === 0}
      keys={rows}
      hasPagination={false}
      emptyText={EMPTY_RESULTS_SHOW}
      ariaLabel="owner-promotional-codes"
    >
      <TableHead>
        <TableRow>
          <TableCell {...headerCellProps} sx={firstColSx}>
            {t(OWNER_PROMOTIONAL_CODES_COLUMN_CODE)}
          </TableCell>
          <TableCell {...headerCellProps}>
            {t(OWNER_PROMOTIONAL_CODES_COLUMN_CREATED)}
          </TableCell>
          <TableCell {...headerCellProps}>
            {t(OWNER_PROMOTIONAL_CODES_COLUMN_REGISTERED)}
          </TableCell>
          <TableCell {...headerCellProps}>
            {t(OWNER_PROMOTIONAL_CODES_COLUMN_MAX_REDEMPTIONS)}
          </TableCell>
          <TableCell {...headerCellProps}>
            {t(OWNER_PROMOTIONAL_CODES_COLUMN_TYPE)}
          </TableCell>
          <TableCell {...headerCellProps}>{t(COLUMN_NAME_IS_ACTIVE)}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => {
          const isLastRow = index === rows.length - 1 && !hasMore;
          const onChangeActive = async (checked) => {
            await onChangeStatus({ codeID: row.id, isActive: checked });
          };
          return (
            <TableRow key={row.id}>
              <TableCell
                {...bodyCellProps}
                isLastRow={isLastRow}
                sx={firstColSx}
              >
                {row.code || "—"}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                {formatCreated({ creationTime: row.creationTime, t })}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                {row.registeredUsersCount ?? 0}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                {row.maxRedemptions ?? "—"}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                {getTypeLabel({
                  type: row.type,
                  metadata: row.metadata,
                  row,
                })}
              </TableCell>
              <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    verticalAlign: "middle",
                  }}
                >
                  <Switch
                    isActive={row.isActive}
                    onChangeIsActive={onChangeActive}
                    hasLabel={false}
                  />
                </Box>
              </TableCell>
            </TableRow>
          );
        })}
        {hasMore && !loading && (
          <TableRow
            ref={lastItemRef}
            sx={{
              "&:hover": {
                backgroundColor: "transparent !important",
              },
            }}
          >
            <TableCell colSpan={6} sx={{ border: 0, py: 2 }}>
              <Box display="flex" justifyContent="center">
                {loadingMore ? <Loader width={28} height={28} /> : null}
              </Box>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </TableContainer>
  );
};

export default PromotionalCodesTable;
